/**
 * The stops, read from the page: each `.stop` section carries its place,
 * coordinates and copy side in `data-*` attributes, and nothing else connects
 * the copy to the globe.
 */
const RAD = Math.PI / 180

/** A stop as the page, the camera and the globe share it. The camera and the globe write the live fields. */
export interface Stop {
  readonly el: HTMLElement
  readonly copySide: 'left' | 'right' | undefined
  readonly global: boolean
  readonly place: string
  readonly where: string
  readonly num: string
  /** The name in the outline and the place list. */
  readonly title: string
  /** Where the place is: degrees, and as a point on the unit sphere. The global stop has none (all 0). */
  readonly lat: number
  readonly lon: number
  readonly x: number
  readonly y: number
  readonly z: number
  /** How present the stop is, 0..1. Written by the camera as the page scrolls, and by the loop as the globe sinks into the dark. */
  act: number
  /** Where the place is on screen, how big a unit of its surface is there, and how visible it is. Written by the globe when it draws. */
  px: number
  py: number
  kk: number
  vis: number
  /** Pre-formatted coordinate label read from the page, e.g. "50.08° N · 14.44° E". */
  readonly coords: string
  /** The place's pin. Written by the pins when they are built. */
  pin?: HTMLElement
  /** The place's entry in the place list. Written by the place list when it attaches to the page. */
  link?: HTMLAnchorElement
  /** The copy beside the place. Written by the globe the first time it is needed. */
  box?: Element | null
}

/** Reads one stop from its section's `data-*` attributes. */
export function parseStop(data: DOMStringMap): Omit<Stop, 'el'> {
  const global = !!data.global
  const lat = global ? 0 : Number(data.lat)
  const lon = global ? 0 : Number(data.lon)
  const phi = lat * RAD
  const lam = lon * RAD
  const cosPhi = Math.cos(phi)
  return {
    copySide: data.copySide === 'left' || data.copySide === 'right' ? data.copySide : undefined,
    global,
    place: data.place ?? '',
    where: data.where ?? '',
    num: data.num ?? '',
    title: data.nav ?? '',
    coords: data.coords ?? '',
    lat,
    lon,
    x: global ? 0 : cosPhi * Math.cos(lam),
    y: global ? 0 : cosPhi * Math.sin(lam),
    z: global ? 0 : Math.sin(phi),
    act: 0,
    vis: 0,
    px: 0,
    py: 0,
    kk: 1,
  }
}

export function readStops(root: ParentNode = document): Stop[] {
  return Array.from(root.querySelectorAll<HTMLElement>('.stop')).map((el) => ({
    el,
    ...parseStop(el.dataset),
  }))
}
